/**
 * ⭐ HOME WIDGETS — the snapping grid, as pure functions (owner, 2026-10-05).
 *
 * Home has 8 pages: the main page (today's Home, unchanged) and one per domain. A
 * domain page is a canvas of widgets on a grid the user arranges:
 *   · 4 columns on a wide canvas, 2 on a narrow one (the CSS decides by a container
 *     query on the canvas; React reads the answer back — see HomeDomainPage);
 *   · fixed rows (ROW_PX, 120 px) with 12–16 px gaps;
 *   · Small = 1 column × 2 rows, Wide = 2 columns × 2 rows (on 2 columns, the full width).
 *
 * ⚠️⚠️ POSITIONS ARE STORED IN 4-COLUMN COORDINATES, always. A 2-column canvas never
 * writes a position: it DRAWS the page packed in reading order (`packForColumns`), and
 * only resize and remove act there. Moving is a 4-column act (a drag or the arrow keys).
 *
 * Nothing here touches React, the DOM or the network, so every rule is tested at its
 * boundaries (homeGrid.test.ts). The store (`homeWidgetsStore`) persists the result in
 * `user_preferences.home_widgets` as `{ version: 1, pages }`.
 *
 * ⭐⚠️ A WIDGET FROM A NEWER VERSION IS KEPT, NEVER ERASED (owner, 2026-10-22). Every save
 * writes the layout WHOLE, so a tab still running an older version would erase any widget
 * type a later release added the first time it saved. Instead a stored widget whose TYPE
 * this version doesn't know (at a size it does know) is an `UnknownWidget`: it keeps its
 * page, cell, id and type string, it OCCUPIES its cells for every rule here (nothing is
 * added, moved, nudged or resized over it), this version never moves, resizes or removes
 * it, and it is written back as stored — its id, type, size and cell, and every other field
 * a later release gave it. The canvas draws it as an inert placeholder ("reload to see it").
 * ⭐⚠️ And when a page changed on both sides, its newer widgets are the CLOUD's, always
 * (`withNewerFromCloud`, review 2026-10-22): the copy this version holds is only ever the
 * one it last read.
 * ⚠️ What is NOT carried: a top-level key other than `pages`, and a `version` above 1 — every
 * write here is `{ version: 1, pages }` (`serializeLayout`). A release that needs either needs
 * its own groundwork release first, as a new type needed this one.
 */

export const GRID_COLS = 4;
/** The deepest a page can go (rows of 120 px): 24 widgets tall. A cap, so a bad row can't run away. */
export const MAX_ROWS = 48;
/** The row height in px — the CSS uses the same number (`.hw-grid`). */
export const ROW_PX = 120;

/** The 7 domain pages, in the orb order (constants/categories). The main page holds no widgets. */
export const HOME_PAGE_IDS = ['personal', 'social', 'occupational', 'avocational', 'ecological', 'intellectual', 'existential'] as const;
export type DomainPageId = typeof HOME_PAGE_IDS[number];
export type HomePageId = 'main' | DomainPageId;

/** ⭐ 'nutrition' (owner, 2026-10-22 — step 5B): the Nutrition widget, the second on Personal.
 *  ⭐ 'supplements' (owner, 2026-10-22 — the Supplements pass, step 5B): the third, on Personal. */
export type WidgetType = 'hydration' | 'nutrition' | 'supplements';
export type WidgetSize = 's' | 'w';

declare const NEWER_TYPE: unique symbol;
/**
 * ⭐ A widget type a NEWER version placed (owner, 2026-10-22) — a non-empty string not in
 * `WIDGET_TYPES`, kept as stored. Branded, so it is never mistaken for a `WidgetType`:
 * `WIDGET_META[w.type]` does not compile until `isKnownWidget(w)` has narrowed it.
 */
export type UnknownWidgetType = string & { readonly [NEWER_TYPE]: true };
interface WidgetPlace { id: string; size: WidgetSize; col: number; row: number }
/** A widget this version can draw and edit. */
export interface KnownWidget extends WidgetPlace { type: WidgetType }
/** A widget from a newer version: it holds its cells, and this version never edits it. */
export interface UnknownWidget extends WidgetPlace { type: UnknownWidgetType }
export type HomeWidget = KnownWidget | UnknownWidget;
export type HomePages = Record<DomainPageId, HomeWidget[]>;
export interface Cell { col: number; row: number }
export interface Rect { col: number; row: number; w: number; h: number }

export const WIDGET_TYPES: readonly WidgetType[] = ['hydration', 'nutrition', 'supplements'];
export const WIDGET_SIZES: readonly WidgetSize[] = ['s', 'w'];
/**
 * Which pages may hold each widget type — the gallery offers it there, and sanitize keeps
 * it only there. Widgets arrive area by area; Hydration, Nutrition and Supplements are the first, on Personal.
 * ⭐ A type this version doesn't know has no entry: it stays on whatever page it is stored
 * on (a newer version decided that — see `sanitizeLayout`).
 */
export const WIDGET_PAGES: Record<WidgetType, readonly DomainPageId[]> = { hydration: ['personal'], nutrition: ['personal'], supplements: ['personal'] };

/** Is `t` a widget type this version knows? */
export const isKnownType = (t: unknown): t is WidgetType =>
  typeof t === 'string' && (WIDGET_TYPES as readonly string[]).includes(t);

/** Can this version draw and edit `w`? False = a newer version's widget (`UnknownWidget`). */
export const isKnownWidget = (w: HomeWidget): w is KnownWidget => isKnownType(w.type);

export const isDomainPage = (id: unknown): id is DomainPageId =>
  typeof id === 'string' && (HOME_PAGE_IDS as readonly string[]).includes(id);

export function emptyPages(): HomePages {
  return Object.fromEntries(HOME_PAGE_IDS.map(id => [id, [] as HomeWidget[]])) as HomePages;
}

/* ── shapes ───────────────────────────────────────────────────────────────── */

/** A size's footprint in cells. Wide is 2 columns on either canvas (= the full width on 2). */
export function dims(size: WidgetSize): { w: number; h: number } {
  return size === 'w' ? { w: 2, h: 2 } : { w: 1, h: 2 };
}

export function rectOf(w: Pick<HomeWidget, 'col' | 'row' | 'size'>): Rect {
  const d = dims(w.size);
  return { col: w.col, row: w.row, w: d.w, h: d.h };
}

/** Do two rectangles share a cell? Touching edges do not. */
export function overlaps(a: Rect, b: Rect): boolean {
  return a.col < b.col + b.w && b.col < a.col + a.w && a.row < b.row + b.h && b.row < a.row + a.h;
}

/** Inside the grid: whole cells, no negative start, no overflow past the last column or row. */
export function inBounds(r: Rect, cols = GRID_COLS, maxRows = MAX_ROWS): boolean {
  return Number.isInteger(r.col) && Number.isInteger(r.row)
    && r.col >= 0 && r.row >= 0 && r.col + r.w <= cols && r.row + r.h <= maxRows;
}

/** Would `r` sit in bounds without covering any widget (other than `ignoreId`)? */
export function fits(widgets: ReadonlyArray<HomeWidget>, r: Rect, cols = GRID_COLS, ignoreId?: string, maxRows = MAX_ROWS): boolean {
  if (!inBounds(r, cols, maxRows)) return false;
  return widgets.every(w => w.id === ignoreId || !overlaps(rectOf(w), r));
}

/** The first cell, in reading order (row, then column), where a widget of `size` fits. */
export function firstFreeSlot(widgets: ReadonlyArray<HomeWidget>, size: WidgetSize, cols = GRID_COLS, ignoreId?: string, maxRows = MAX_ROWS): Cell | null {
  const d = dims(size);
  if (d.w > cols) return null;
  for (let row = 0; row + d.h <= maxRows; row++) {
    for (let col = 0; col + d.w <= cols; col++) {
      if (fits(widgets, { col, row, w: d.w, h: d.h }, cols, ignoreId, maxRows)) return { col, row };
    }
  }
  return null;
}

/** Reading order: row, then column (then id, so the order is total). */
export function sortReading<T extends Pick<HomeWidget, 'col' | 'row' | 'id'>>(widgets: ReadonlyArray<T>): T[] {
  return [...widgets].sort((a, b) => a.row - b.row || a.col - b.col || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** How many rows the widgets use (the lowest edge); 0 for an empty page. */
export function rowsUsed(widgets: ReadonlyArray<Pick<HomeWidget, 'row' | 'size'>>): number {
  return widgets.reduce((n, w) => Math.max(n, w.row + dims(w.size).h), 0);
}

/* ── the pointer ──────────────────────────────────────────────────────────── */

/** What a canvas measures as: its width, its column count, the row height and the gap. */
export interface GridMetrics { width: number; cols: number; rowHeight: number; gap: number }

/** One column's width, and the distance from one column (row) start to the next. */
export function pitch(m: GridMetrics): { colWidth: number; x: number; y: number } {
  const colWidth = Math.max(0, (m.width - (m.cols - 1) * m.gap) / m.cols);
  return { colWidth, x: colWidth + m.gap, y: m.rowHeight + m.gap };
}

/**
 * The cell under a point, in px from the grid's top-left. A gap belongs to the cell
 * before it. Clamped into the grid (a point left of or above it reads column / row 0;
 * right of it, the last column), so a drag never asks for a negative cell.
 */
export function cellFromPoint(x: number, y: number, m: GridMetrics, maxRows = MAX_ROWS): Cell {
  const p = pitch(m);
  const col = p.x > 0 ? Math.floor(x / p.x) : 0;
  const row = p.y > 0 ? Math.floor(y / p.y) : 0;
  return {
    col: Math.min(m.cols - 1, Math.max(0, Number.isFinite(col) ? col : 0)),
    row: Math.min(maxRows - 1, Math.max(0, Number.isFinite(row) ? row : 0)),
  };
}

/**
 * Where a dragged widget lands: the cell NEAREST its top-left corner — the pointer less
 * the offset at which it was grabbed. Snapping by the corner (not by the pointer's cell)
 * keeps a widget from jumping by a column when it is grabbed by its right half.
 */
export function dropCell(left: number, top: number, m: GridMetrics, maxRows = MAX_ROWS): Cell {
  const p = pitch(m);
  return cellFromPoint(left + p.x / 2, top + p.y / 2, m, maxRows);
}

/* ── edits (each returns a NEW array, or a refusal; the input is never mutated) ──
   ⭐ A newer version's widget is never the one edited (refused), and always an obstacle:
   `fits` checks EVERY widget on the page, whatever its type (owner, 2026-10-22). */

/** Move `id` to `to`, or null when it would overlap or overflow there (or is a newer version's widget). */
export function moveWidget(widgets: ReadonlyArray<HomeWidget>, id: string, to: Cell, cols = GRID_COLS): HomeWidget[] | null {
  const w = widgets.find(x => x.id === id);
  if (!w || !isKnownWidget(w)) return null;
  const d = dims(w.size);
  if (!fits(widgets, { col: to.col, row: to.row, w: d.w, h: d.h }, cols, id)) return null;
  return widgets.map(x => (x.id === id ? { ...x, col: to.col, row: to.row } : x));
}

/**
 * The arrow keys: step one cell in a direction, hopping over a widget in the way to the
 * NEXT place it fits along that line (never overlapping, never overflowing). Null at the
 * edge, or when nothing fits before it.
 */
export function nudgeWidget(widgets: ReadonlyArray<HomeWidget>, id: string, dx: number, dy: number, cols = GRID_COLS): { widgets: HomeWidget[]; at: Cell } | null {
  const w = widgets.find(x => x.id === id);
  if (!w || !isKnownWidget(w) || (dx === 0 && dy === 0)) return null;
  const d = dims(w.size);
  for (let k = 1; k <= Math.max(cols, MAX_ROWS); k++) {
    const r = { col: w.col + dx * k, row: w.row + dy * k, w: d.w, h: d.h };
    if (!inBounds(r, cols)) return null;
    if (fits(widgets, r, cols, id)) {
      const at = { col: r.col, row: r.row };
      return { widgets: widgets.map(x => (x.id === id ? { ...x, ...at } : x)), at };
    }
  }
  return null;
}

export type ResizeResult =
  | { kind: 'in-place' | 'moved'; widgets: HomeWidget[]; at: Cell }
  | { kind: 'refused' };

/**
 * Small ⇄ Wide. In place when the new footprint fits where the widget is; otherwise at
 * the first free slot (reading order); refused only when the page has no room at all.
 */
export function resizeWidget(widgets: ReadonlyArray<HomeWidget>, id: string, size: WidgetSize, cols = GRID_COLS): ResizeResult {
  const w = widgets.find(x => x.id === id);
  if (!w || !isKnownWidget(w)) return { kind: 'refused' };
  const d = dims(size);
  if (w.size === size) return { kind: 'in-place', widgets: [...widgets], at: { col: w.col, row: w.row } };
  if (fits(widgets, { col: w.col, row: w.row, w: d.w, h: d.h }, cols, id)) {
    return { kind: 'in-place', widgets: widgets.map(x => (x.id === id ? { ...x, size } : x)), at: { col: w.col, row: w.row } };
  }
  const slot = firstFreeSlot(widgets, size, cols, id);
  if (!slot) return { kind: 'refused' };
  return { kind: 'moved', widgets: widgets.map(x => (x.id === id ? { ...x, size, ...slot } : x)), at: slot };
}

/** Add a widget (of a type this version knows) at the first free slot, or null when the page is full. */
export function addWidget(widgets: ReadonlyArray<HomeWidget>, w: Pick<KnownWidget, 'id' | 'type' | 'size'>, cols = GRID_COLS): HomeWidget[] | null {
  const slot = firstFreeSlot(widgets, w.size, cols);
  return slot ? [...widgets, { ...w, ...slot }] : null;
}

/**
 * A narrow canvas draws the page PACKED: the widgets in reading order, each at the first
 * free slot of a `cols`-column grid (dense — a Small can fill the hole beside an earlier
 * Small). For display only: the stored 4-column positions are never rewritten from this.
 * On the full 4 columns it is the identity.
 */
export function packForColumns(widgets: ReadonlyArray<HomeWidget>, cols: number): HomeWidget[] {
  if (cols >= GRID_COLS) return [...widgets];
  const placed: HomeWidget[] = [];
  const maxRows = MAX_ROWS * Math.ceil(GRID_COLS / Math.max(1, cols));
  for (const w of sortReading(widgets)) {
    const slot = firstFreeSlot(placed, w.size, cols, undefined, maxRows);
    if (slot) placed.push({ ...w, ...slot });
  }
  return placed;
}

/* ── storage ──────────────────────────────────────────────────────────────── */

export interface StoredLayout { version: 1; pages: HomePages }

/**
 * What the column stores. ⚠️ Only `pages`: a top-level key a later release added, or a
 * `version` above 1, is not written back (see the header) — every field of every WIDGET is.
 */
export function serializeLayout(pages: HomePages): StoredLayout {
  return { version: 1, pages };
}

/** A fresh widget id (`w-<uuid>`). */
export const newWidgetId = (): string =>
  (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    ? `w-${crypto.randomUUID()}`
    : `w-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;


const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * ⚠️⚠️ WHATEVER IS STORED, THE CANVAS GETS A LAYOUT IT CAN DRAW. The value comes from
 * the database (and was written by some version of this app, or by hand):
 *   · anything that is not `{ pages: { … } }` → every page empty;
 *   · an unknown page id or size → dropped; a KNOWN type on a page that does not offer
 *     it (`WIDGET_PAGES`) → dropped;
 *   · ⭐ an unknown TYPE (a non-empty string not in `WIDGET_TYPES`) at a known size →
 *     KEPT, on whatever page it is stored on (owner, 2026-10-22) — a newer version's
 *     widget. This version can't draw it, so it never moves it either: unknown widgets are
 *     placed FIRST, at their own cells, and the known ones give way around them;
 *   · ⭐ every widget keeps the fields this version doesn't read (review, 2026-10-22): a
 *     later release's per-widget setting rides along, on a newer widget or on Hydration;
 *   · a position that is not a whole number → rounded; out of the grid → clamped in;
 *   · a missing or repeated id → a fresh one;
 *   · ONE widget of each KNOWN type at most (the first in page order, then reading order).
 *     ⭐ A type this version doesn't know is not limited (review, 2026-10-22): a newer release
 *     may allow several, and dropping one here would erase it on this tab's next save of any
 *     page — kept, they are inert and only hold their cells;
 *   · overlaps → resolved in reading order: a widget that collides with an earlier one
 *     moves to the first free slot (dropped only if the page is full);
 *   · each page comes back in reading order of the final cells.
 * Nothing is written back here — the next real change saves the clean value.
 *
 * ⚠️ Why an unknown SIZE or PAGE is still dropped (unlike a type): a size IS a footprint,
 * and every rule here reasons in footprints — with no `dims` for it this version could
 * neither draw it nor keep an add or a move off its cells. The pages are the 7 life domains
 * (constants/categories), fixed by the model, not by a release. So the next release adds a
 * TYPE at the existing sizes (the Nutrition widget); a new size or page would need its own
 * groundwork release first, as this one is for types.
 */
export function sanitizeLayout(
  raw: unknown,
  makeId: () => string = newWidgetId,
  /** `onePerType: false` lifts the one-of-each rule — for tests of the overlap and id rules. */
  { onePerType = true }: { onePerType?: boolean } = {},
): HomePages {
  const out = emptyPages();
  const source = isObj(raw) && isObj(raw.pages) ? raw.pages : null;
  if (!source) return out;
  const seenIds = new Set<string>();
  const seenTypes = new Set<string>();
  for (const page of HOME_PAGE_IDS) {
    const list = source[page];
    if (!Array.isArray(list)) continue;
    const candidates: HomeWidget[] = [];
    for (const item of list) {
      if (!isObj(item)) continue;
      const type = item.type;
      const size = item.size as WidgetSize;
      if (typeof type !== 'string' || !type.trim() || !WIDGET_SIZES.includes(size)) continue;
      const known = isKnownType(type);
      if (known && !WIDGET_PAGES[type].includes(page)) continue;
      const d = dims(size);
      const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : 0);
      const col = Math.min(GRID_COLS - d.w, Math.max(0, num(item.col)));
      const row = Math.min(MAX_ROWS - d.h, Math.max(0, num(item.row)));
      let id = typeof item.id === 'string' && item.id.trim() ? item.id : '';
      if (!id || seenIds.has(id)) id = makeId();
      seenIds.add(id);
      // The stored record, with the five fields this version owns set clean: any other
      // field (a later release's) is carried, never dropped.
      candidates.push(known
        ? { ...item, id, type, size, col, row }
        : { ...item, id, type: type as UnknownWidgetType, size, col, row });
    }
    const placed: HomeWidget[] = [];
    // ⭐ A newer version's widgets first: they keep their cells; this version's own give way.
    const order = [...sortReading(candidates.filter(w => !isKnownWidget(w))), ...sortReading(candidates.filter(isKnownWidget))];
    for (const w of order) {
      if (onePerType && isKnownWidget(w) && seenTypes.has(w.type)) continue;
      if (fits(placed, rectOf(w))) { placed.push(w); seenTypes.add(w.type); continue; }
      const slot = firstFreeSlot(placed, w.size);
      if (slot) { placed.push({ ...w, ...slot }); seenTypes.add(w.type); }
    }
    // in reading order of where each one ended up (the DOM order, so the tab order, follows it)
    out[page] = sortReading(placed);
  }
  return out;
}

/** The fields of a widget this version owns. Any other field is a later release's. */
const OWN_FIELDS: ReadonlySet<string> = new Set(['id', 'type', 'size', 'col', 'row']);

/**
 * ⭐⚠️ THE MERGE'S LAST WORD: A NEWER VERSION'S WIDGETS ARE THE CLOUD'S, ALWAYS (review, 2026-10-22).
 *
 * `merged` = the 3-way merge of this tab's layout into the cloud's, page by page (a page
 * changed on both sides keeps this tab's copy whole — two grids mixed widget by widget could
 * overlap). But this version never edits a newer widget, so the copy of one it holds is only
 * ever the one it last read: on a page changed on both sides, keeping it would erase, revert
 * or bring back what a newer device did — exactly when a tab on this version is the OLDER tab
 * (right after the next release ships; a desktop window that stays focused never refreshes).
 * So, on every page:
 *   · a newer widget is the cloud's — added, moved, removed or edited there, it stands as it
 *     is there;
 *   · this version's widgets are the merge's (this tab's move, resize or remove lands), and a
 *     field on one that this version doesn't read is the cloud's (a later release's setting
 *     on Hydration, say) — this version never changes one;
 *   · overlaps are `sanitizeLayout`'s: the newer widgets are placed first, this version's
 *     give way to the first free slot.
 * With no newer widget anywhere, this is the merge as it was (re-sanitized).
 */
export function withNewerFromCloud(merged: HomePages, cloud: HomePages): HomePages {
  const cloudById = new Map<string, HomeWidget>();
  for (const page of HOME_PAGE_IDS) for (const w of cloud[page] ?? []) cloudById.set(w.id, w);
  const theirs = (w: KnownWidget): KnownWidget => {
    const c = cloudById.get(w.id);
    if (!c) return w;
    const extra = Object.fromEntries(Object.entries(c).filter(([k]) => !OWN_FIELDS.has(k)));
    return { ...extra, id: w.id, type: w.type, size: w.size, col: w.col, row: w.row };
  };
  const pages = emptyPages();
  for (const page of HOME_PAGE_IDS) {
    pages[page] = [
      ...(cloud[page] ?? []).filter(w => !isKnownWidget(w)),
      ...(merged[page] ?? []).filter(isKnownWidget).map(theirs),
    ];
  }
  return sanitizeLayout(serializeLayout(pages));
}

/** Where a widget of `type` is placed, if anywhere (the first match in page order). Never a newer version's widget. */
export function findWidget(pages: HomePages, type: WidgetType): { page: DomainPageId; widget: KnownWidget } | null {
  for (const page of HOME_PAGE_IDS) {
    const widget = pages[page]?.find((w): w is KnownWidget => isKnownWidget(w) && w.type === type);
    if (widget) return { page, widget };
  }
  return null;
}
